import { cn } from '@workspace/lib/utils';
import { buildDatUrl, buildSrcSet, buildWidthSrcSet, resolveAlt, resolveSource } from '@workspace/lib/picture.utils';
import type { PictureSource } from '@workspace/lib/picture.utils';

export interface PictureProps {
  image?: PictureSource;
  src?: string;
  alt?: string;
  desktopWidth: number;
  desktopHeight: number;
  tabletWidth?: number;
  tabletHeight?: number;
  tabletBreakpoint?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  mobileBreakpoint?: number;
  className?: string;
  imgClassName?: string;
  draggable?: boolean;
  ariaHidden?: boolean;
  priority?: boolean;
  loading?: 'eager' | 'lazy';
  fill?: boolean;
  objectPosition?: string;
  /** Rendered width for fluid images (e.g. `100vw`). Omit for fixed-size images. */
  sizes?: string;
}

export function Picture({
  image,
  src,
  alt,
  desktopWidth,
  desktopHeight,
  tabletWidth,
  tabletHeight,
  tabletBreakpoint = 1280,
  mobileWidth,
  mobileHeight,
  mobileBreakpoint = 768,
  className,
  imgClassName,
  draggable,
  ariaHidden,
  priority = false,
  loading,
  fill = false,
  objectPosition,
  sizes,
}: PictureProps) {
  const resolved = image ? resolveSource(image) : undefined;
  const baseUrl = resolved?.baseUrl || src || '';
  const shouldTransform = !!resolved?.isBynder && !resolved.isSvg && !!resolved.baseUrl;

  if (!baseUrl) return null;

  const effectiveMobileWidth = mobileWidth ?? desktopWidth;
  const effectiveMobileHeight = mobileHeight ?? desktopHeight;
  const hasTabletSource = !!(tabletWidth && tabletHeight);
  const hasMobileSource =
    hasTabletSource || effectiveMobileWidth !== desktopWidth || effectiveMobileHeight !== desktopHeight;

  // Plain 1x URL for the `src` attribute fallback
  const imgW = hasMobileSource ? effectiveMobileWidth : desktopWidth;
  const imgH = hasMobileSource ? effectiveMobileHeight : desktopHeight;
  const imgSrc = shouldTransform ? buildDatUrl(baseUrl, imgW, imgH) : baseUrl;
  const imgWidth = imgW;
  const imgHeight = imgH;

  const getSrcSet = (width: number, height: number) =>
    sizes
      ? buildWidthSrcSet(baseUrl, width, height, shouldTransform)
      : buildSrcSet(baseUrl, width, height, shouldTransform);
  const sizesAttr = sizes && shouldTransform ? sizes : undefined;

  return (
    <picture className={cn(fill && 'absolute inset-0 size-full', className)}>
      {hasMobileSource && (
        <source
          media={`(min-width: ${hasTabletSource ? tabletBreakpoint : mobileBreakpoint}px)`}
          srcSet={getSrcSet(desktopWidth, desktopHeight)}
          sizes={sizesAttr}
          width={desktopWidth}
          height={desktopHeight}
        />
      )}
      {hasTabletSource && (
        <source
          media={`(min-width: ${mobileBreakpoint}px)`}
          srcSet={getSrcSet(tabletWidth!, tabletHeight!)}
          sizes={sizesAttr}
          width={tabletWidth!}
          height={tabletHeight!}
        />
      )}
      <img
        src={imgSrc}
        srcSet={shouldTransform ? getSrcSet(imgWidth, imgHeight) : undefined}
        sizes={sizesAttr}
        alt={image ? resolveAlt(image, alt) : (alt ?? '')}
        width={imgWidth}
        height={imgHeight}
        loading={priority ? 'eager' : (loading ?? 'lazy')}
        fetchPriority={priority ? 'high' : undefined}
        decoding="async"
        aria-hidden={ariaHidden}
        draggable={draggable}
        className={cn('w-full h-full object-cover', imgClassName)}
        style={objectPosition ? { objectPosition } : undefined}
      />
    </picture>
  );
}
